import api from 'api'

export interface IRolesListParams {
  page?: number
  perPage?: number
  criteria?: string
  order?: string
  fieldToOrder?: string
}

export interface IRolesListResponse {
  roles: import('interfaces/roles').IRole[]
  total: number
}

const buildRolesQuery = ({
  page = 1,
  perPage = 10,
  criteria = '',
  order = 'asc',
  fieldToOrder = 'name',
}: IRolesListParams = {}) => ({
  page,
  per_page: perPage,
  q: {
    s: [`${fieldToOrder} ${order}`],
    m: 'and',
    ...(criteria ? { name_or_code_or_description_cont: criteria } : {}),
  },
})

const getRoles = (params: IRolesListParams = {}) => {
  return api.post<IRolesListResponse>(
    'api/v1/roles/list',
    buildRolesQuery(params)
  )
}

const getRoleOptions = () => {
  return getRoles({
    page: 1,
    perPage: 500,
    criteria: '',
    order: 'asc',
    fieldToOrder: 'name',
  })
}

const removeRole = (id: number) => {
  return api.delete(`api/v1/roles/${id}`)
}

const getCoursesByRole = (roleId: number) => {
  return api.get(`api/v1/roles/${roleId}/courses`)
}

const getUsersByRole = (
  roleId: number,
  page: number,
  perPage: number,
  criteria: string
) => {
  const term = criteria.trim()
  const data: {
    page: number
    per_page: number
    q?: { m: string; name_and_lastname_or_identifier_cont: string }
  } = {
    page,
    per_page: perPage,
  }
  if (term) {
    data.q = {
      m: 'and',
      name_and_lastname_or_identifier_cont: term,
    }
  }
  return api.post<{
    users: {
      id: number
      firstName: string
      lastName: string
      avatar: string
      identifier: string
    }[]
    total: number
  }>(`api/v1/roles/${roleId}/users`, data)
}

const getRoleDetail = (roleId: string) => {
  return api.get(`api/v1/roles/${roleId}`)
}

const saveRole = (data: import('interfaces/roles').IRoleDetail) => {
  const { learningPrograms, courses, ...restData } = data
  const transformedData = {
    ...restData,
    learningProgramIds: learningPrograms.map((program) => program.id),
    courseIds: courses.map((course) => course.id),
  }

  return api.post('api/v1/roles', transformedData)
}

const updateRole = (
  roleId: string,
  data: import('interfaces/roles').IRoleDetail
) => {
  const { learningPrograms, courses, ...restData } = data
  const transformedData = {
    ...restData,
    learningProgramIds: learningPrograms.map((program) => program.id),
    courseIds: courses.map((course) => course.id),
  }

  return api.put(`api/v1/roles/${roleId}`, transformedData)
}

const getCoursesByLearningProgram = (learningProgramId: number) => {
  const data = { learning_path_id: learningProgramId }
  return api.post('api/v1/roles/learning_path_courses', data)
}

const getCourses = (
  page: number,
  criteria: string,
  order: string,
  fieldToOrder: string
) => {
  const data = {
    page: page,
    per_page: 10,
    q: {
      s: [`${fieldToOrder} ${order}`],
      m: 'and',
      name_cont: criteria,
    },
  }
  return api.post('api/v1/roles/search_courses', data)
}

const getProfiles = () => {
  return api.get('administration/lms_profiles/list')
}

const getPermissions = () => {
  return api.get<import('interfaces/currentPermissions').ICurrentPermissions>(
    'api/v1/roles/current'
  )
}

export interface UserRoleAccess {
  role: { id: number; name: string; isDefault: boolean } | null
  accessLevel: string | null
  profiles: { id: number; name: string; color?: string }[]
  accessToCourses: number
  courses: { id: number; name: string }[]
  accessToLearningPrograms: number
  learningPrograms: { id: number; name: string }[]
  accesses: Record<string, boolean>
}

const getUserAccess = (userId: number) => {
  return api.get<UserRoleAccess>(`api/v1/roles/users/${userId}/access`)
}

export const rolesService = {
  getRoles,
  getRoleOptions,
  removeRole,
  getCoursesByRole,
  getUsersByRole,
  getRoleDetail,
  saveRole,
  updateRole,
  getCoursesByLearningProgram,
  getCourses,
  getProfiles,
  getPermissions,
  getUserAccess,
}
